import Page from '../../../shared/components/Page/Page';
import {Card, Col, Row, DatePicker, Radio, Space, Table, Tag} from "antd";
import {useContext, useEffect, useState} from "react";
import LoadingContext from "../../../app/contexts/LoadingContext";
import { calculateProfit } from "../../dashboard/api";
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
const { RangePicker } = DatePicker;
function Profit() {
    const {setLoading} = useContext(LoadingContext);
    const [dataTable, setDataTable] = useState([]);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(6, 'day').startOf('day'),
        dayjs().endOf('day')
    ]);
    const [quickSelect, setQuickSelect] = useState('7days');
    const columns = [
        {
            title: 'Ngày',
            dataIndex: 'date',
            key: 'date',
            render: (text) => dayjs(text).format('DD/MM/YYYY')
        },
        {
            title: 'Tổng tiền nạp',
            dataIndex: 'depositAmount',
            key: 'depositAmount',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Tổng tiền rút',
            dataIndex: 'withdrawAmount',
            key: 'withdrawAmount',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Phí nạp',
            dataIndex: 'depositFee',
            key: 'depositFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Phí rút',
            dataIndex: 'withdrawFee',
            key: 'withdrawFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Tổng phí',
            dataIndex: 'totalFee',
            key: 'totalFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Lợi nhuận',
            dataIndex: 'profit',
            key: 'profit',
            align: 'right',
            render: (value) => {
                const color = value >= 0 ? 'green' : 'red';
                return <Tag color={color}>{value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}</Tag>
            }
        }
    ];
    const fetchProfit = async (startDate, endDate) => {
        setLoading(true);
        const response = await calculateProfit( {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
        });
        if (response.success) {
            // Transform data from the new API response format
            const tableData = response.data.dailyStats.map(item => ({
                key: item.date,
                date: item.date,
                depositAmount: parseFloat(item.deposits) || 0,
                withdrawAmount: parseFloat(item.withdraws) || 0,
                depositFee: parseFloat(item.feeIn) || 0,
                withdrawFee: parseFloat(item.feeOut) || 0,
                totalFee: parseFloat(item.feeIn + item.feeOut) || 0,
                profit: parseFloat(item.netProfit) || 0
            }));
            setDataTable(tableData);
        }
        setLoading(false);
    }
    useEffect(() => {
        if (dateRange) {
            fetchProfit(dateRange[0], dateRange[1]).finally();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);
    const handleQuickSelectChange = (e) => {
        const value = e.target.value;
        setQuickSelect(value);
        
        let start, end;
        switch (value) {
            case '7days':
                start = dayjs().subtract(6, 'day').startOf('day');
                end = dayjs().endOf('day');
                break;
            case 'thisWeek':
                start = dayjs().startOf('week');
                end = dayjs().endOf('day');
                break;
            case 'lastWeek':
                start = dayjs().subtract(1, 'week').startOf('week');
                end = dayjs().subtract(1, 'week').endOf('week');
                break;
            case 'thisMonth':
                start = dayjs().startOf('month');
                end = dayjs().endOf('day');
                break;
            case 'lastMonth':
                start = dayjs().subtract(1, 'month').startOf('month');
                end = dayjs().subtract(1, 'month').endOf('month');
                break;
            default:
                return;
        }
        setDateRange([start, end]);
    };
    const handleRangePickerChange = (dates) => {
        setQuickSelect(null);
        setDateRange(dates);
    };
    return (
        <Page title="Lợi nhuận">
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card
                        title={'Thống kê lợi nhuận'}
                        bordered={true}
                        className="dashboard-card"
                        extra={
                            <Space>
                                <Radio.Group value={quickSelect} onChange={handleQuickSelectChange}>
                                    <Radio.Button value="7days">7 ngày qua</Radio.Button>
                                    <Radio.Button value="thisWeek">Tuần này</Radio.Button>
                                    <Radio.Button value="lastWeek">Tuần trước</Radio.Button>
                                    <Radio.Button value="thisMonth">Tháng này</Radio.Button>
                                    <Radio.Button value="lastMonth">Tháng trước</Radio.Button>
                                </Radio.Group>
                                <RangePicker 
                                    value={dateRange}
                                    onChange={handleRangePickerChange}
                                    format="DD/MM/YYYY"
                                />
                            </Space>
                        }
                    >
                        <Table 
                            columns={columns} 
                            dataSource={dataTable}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            className="dashboard-table"
                            summary={(pageData) => {
                                const totals = pageData.reduce(
                                    (acc, curr) => ({
                                        depositAmount: acc.depositAmount + Number(curr.depositAmount || 0),
                                        withdrawAmount: acc.withdrawAmount + Number(curr.withdrawAmount || 0),
                                        depositFee: acc.depositFee + Number(curr.depositFee || 0),
                                        withdrawFee: acc.withdrawFee + Number(curr.withdrawFee || 0),
                                        totalFee: acc.totalFee + Number(curr.totalFee || 0),
                                        profit: acc.profit + Number(curr.profit || 0)
                                    }),
                                    { depositAmount: 0, withdrawAmount: 0, depositFee: 0, withdrawFee: 0, totalFee: 0, profit: 0 }
                                );
                                return (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                {totals.depositAmount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="right">
                                                {totals.withdrawAmount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="right">
                                                {totals.depositFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} align="right">
                                                {totals.withdrawFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} align="right">
                                                {totals.totalFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right">
                                                <Tag color={totals.profit >= 0 ? 'green' : 'red'}>
                                                    {totals.profit.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                                </Tag>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />
                    </Card>
                </Col>
            </Row>
        </Page>
    );
}
export default Profit; 